import type {
	IArvoreRestricoesResponse,
	ISalvarRestricaoItem,
	IVerificaRecursosDaTelaResponse,
} from "@/api/space/dtos/authenticator.dto";

/** Delta de alteracoes separado por tipo, no formato esperado pelo backend de salvar. */
export type DeltaRestricoes = {
	recursos: ISalvarRestricaoItem[]; // telas (acesso)
	permissoes: ISalvarRestricaoItem[]; // sub-ações
};

/** Map `tagPermissao -> ativo` — a `tag` da permissao e a chave de identidade da matriz. */
export type EstadoRecursos = Map<string, boolean>;

/**
 * Map `tag -> ativo` a partir da arvore. Inclui o proprio recurso/tela (acesso) e cada permissao,
 * pois ambos sao toggles salvos por `tag`.
 */
export function construirMapaEstado(arvore: IArvoreRestricoesResponse): EstadoRecursos {
	const mapa: EstadoRecursos = new Map();
	arvore.forEach((modulo) =>
		modulo.recursos.forEach((recurso) => {
			mapa.set(recurso.tag, recurso.ativo);
			recurso.permissoes.forEach((permissao) => mapa.set(permissao.tag, permissao.ativo));
		}),
	);
	return mapa;
}

/** Set com as `tag` que sao recursos (telas) na arvore — usado para classificar o delta. */
function construirSetRecursos(arvore: IArvoreRestricoesResponse | null | undefined): Set<string> {
	const tags = new Set<string>();
	(arvore ?? []).forEach((modulo) => modulo.recursos.forEach((recurso) => tags.add(recurso.tag)));
	return tags;
}

/**
 * Tags cujo estado mudou entre o salvo (snapshot) e o atual, separadas por tipo: cada tag presente
 * na arvore como recurso (tela) vai para `recursos`; as demais (permissoes) vao para `permissoes`.
 */
export function calcularDelta(
	arvore: IArvoreRestricoesResponse | null | undefined,
	snapshot: EstadoRecursos,
	atual: EstadoRecursos,
): DeltaRestricoes {
	const tagsRecurso = construirSetRecursos(arvore);
	const recursos: ISalvarRestricaoItem[] = [];
	const permissoes: ISalvarRestricaoItem[] = [];

	atual.forEach((ativo, tag) => {
		if ((snapshot.get(tag) ?? false) === ativo) return;
		const item: ISalvarRestricaoItem = { tag, ativo };
		if (tagsRecurso.has(tag)) recursos.push(item);
		else permissoes.push(item);
	});

	return { recursos, permissoes };
}

export function contarAlteracoes(snapshot: EstadoRecursos, atual: EstadoRecursos): number {
	let total = 0;
	atual.forEach((ativo, tag) => {
		if ((snapshot.get(tag) ?? false) !== ativo) total += 1;
	});
	return total;
}

// ----------------------------------- RESUMO DETALHADO DAS ALTERACOES (PRE-SALVAR) ----------------------------------
// As chaves `id` abaixo sao strings (tag da permissao, tag do recurso e descricao do modulo).
export type AlteracaoRecursoVM = { id: string; descricao: string; para: boolean };
export type AlteracaoTelaVM = { id: string; descricao: string; recursos: AlteracaoRecursoVM[] };
export type AlteracaoModuloVM = { id: string; descricao: string; telas: AlteracaoTelaVM[] };
export type ResumoAlteracoes = {
	grupos: AlteracaoModuloVM[];
	ativados: number;
	desativados: number;
	total: number;
};

/**
 * Resumo legivel das alteracoes pendentes (snapshot salvo -> estado atual), agrupado por
 * modulo -> recurso(tela) -> permissao, para exibir no dialog de confirmacao antes de salvar. Cruza
 * com a arvore para recuperar as descricoes; considera apenas permissoes cujo estado mudou.
 */
export function montarResumoAlteracoes(
	arvore: IArvoreRestricoesResponse | null | undefined,
	snapshot: EstadoRecursos,
	atual: EstadoRecursos,
): ResumoAlteracoes {
	const grupos: AlteracaoModuloVM[] = [];
	let ativados = 0;
	let desativados = 0;

	(arvore ?? []).forEach((modulo) => {
		const telasAlteradas: AlteracaoTelaVM[] = [];

		modulo.recursos.forEach((recurso) => {
			const recursosAlterados: AlteracaoRecursoVM[] = [];

			const paraRecurso = atual.get(recurso.tag) ?? false;
			const deRecurso = snapshot.get(recurso.tag) ?? false;
			if (deRecurso !== paraRecurso) {
				if (paraRecurso) ativados += 1;
				else desativados += 1;
				recursosAlterados.push({ id: recurso.tag, descricao: "Acesso à tela", para: paraRecurso });
			}

			recurso.permissoes.forEach((permissao) => {
				const para = atual.get(permissao.tag) ?? false;
				const de = snapshot.get(permissao.tag) ?? false;
				if (de === para) return;

				if (para) ativados += 1;
				else desativados += 1;

				recursosAlterados.push({ id: permissao.tag, descricao: permissao.descricao, para });
			});

			if (recursosAlterados.length > 0)
				telasAlteradas.push({ id: recurso.tag, descricao: recurso.titulo, recursos: recursosAlterados });
		});

		if (telasAlteradas.length > 0)
			grupos.push({ id: modulo.descricao, descricao: modulo.descricao, telas: telasAlteradas });
	});

	return { grupos, ativados, desativados, total: ativados + desativados };
}

/**
 * Converte a resposta de "verifica recursos da tela" (modal: 1 recurso + suas permissoes) numa arvore
 * de 1 modulo / 1 recurso, para reutilizar `construirMapaEstado` / `calcularDelta` / `montarResumoAlteracoes`
 * — os mesmos utilitarios usados pela tela de cadastro de restricoes (que opera sobre a arvore completa).
 */
export function AdapterRecursoDaTelaComoArvore(data: IVerificaRecursosDaTelaResponse): IArvoreRestricoesResponse {
	return [
		{
			descricao: data.recurso.descricao,
			recursos: [
				{
					tag: data.recurso.tag,
					titulo: data.recurso.descricao,
					descricao: data.recurso.descricao,
					ativo: data.recurso.ativo,
					permissoes: data.permissoes,
				},
			],
		},
	];
}
